import Highcharts from "Static/js/highstock";
import { MEGABYTE } from "constant/app";
import each from "lodash/each";
import PropTypes from "prop-types";
import React from "react";
import * as App from "redux-store/applications/selectors";
import { utcToLocal } from "util/time";

const BS_APP_ID = -1;
const PLAYER_APP_ID = -2;
const GPU_APP_ID = -3;

class MemoryChart extends React.PureComponent {
  static get propTypes() {
    return {
      data: PropTypes.array.isRequired,
      applications: PropTypes.object,
    };
  }

  constructor(props) {
    super(props);

    this.chartElem = null;
    this.highchart = null;

    this.destroy = this.destroy.bind(this);
  }

  componentDidUpdate() {
    this._renderChart(this.props);
  }

  componentDidMount() {
    this._renderChart(this.props);
  }

  componentWillUnmount() {
    this.destroy();
  }

  destroy() {
    if (this.highchart) {
      this.highchart.destroy();
    }
  }

  _getFakeAppName(appId) {
    switch (appId) {
      case BS_APP_ID:
        return "Black Screen";
      case PLAYER_APP_ID:
        return "Player";
      case GPU_APP_ID:
        return "GPU";
      default:
        return null;
    }
  }

  _getAppName(props, appId) {
    appId = parseInt(appId, 10);
    let appName = this._getFakeAppName(appId);
    if (!appName) {
      const app = props.applications[appId];

      appName = app ? App.name(app) : appId;
    }

    return appName;
  }

  _getData(props) {
    const data = props.data.reduce((acc, p) => {
      const [ts, appId, bytes] = p;
      // Memory usage data is in bytes but we display as MB on fleet.
      const usage = bytes / MEGABYTE;

      if (!acc[appId]) {
        acc[appId] = [];
      }

      acc[appId].push([ts, usage]);

      return acc;
    }, {});

    return Object.entries(data).map(([appId, tsData]) => {
      return {
        name: this._getAppName(props, appId),
        data: tsData,
      };
    });
  }

  _renderChart(props) {
    const noData = props.data.length === 0;
    if (noData) {
      return;
    }

    const { chartElem } = this;
    const opts = {
      chart: {
        renderTo: chartElem,
        type: "spline",
      },
      title: "Memory Usage per Application",
      xAxis: {
        type: "datetime",
        labels: {
          enabled: true,
        },
      },
      tooltip: {
        formatter: function () {
          // Do not make this an arrow function.
          let t = utcToLocal(this.points[0].x) + "<br>";

          each(this.points, (p) => {
            t += `${p.series.name}: <b>${p.y.toFixed(2)} MB</b><br>`;
          });

          return t;
        },
        shared: true,
      },
      yAxis: {
        title: "Memory Usage (MB)",
        type: "category",
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        startOnTick: false,
        showFirstLabel: true,
        showLastLabel: true,
      },
      series: this._getData(props),
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {
          lineWidth: 1,
          marker: {
            enabled: false,
          },
        },
      },
    };

    this.destroy();

    this.highchart = new Highcharts.Chart(opts);
  }

  render() {
    const noData = this.props.data.length === 0;

    return (
      <div
        className="chart memory-chart"
        ref={(elem) => {
          this.chartElem = elem;
        }}
      >
        {noData && <div>No data for chart.</div>}
      </div>
    );
  }
}

export { MemoryChart };
